import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2, ArrowLeft } from 'lucide-react';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/blackified-logo.png";

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setErrorMessage('Invalid verification link. No token provided.');
        return;
      }

      try {
        const response = await fetch(`${API}/auth/verify-email/${token}`, {
          credentials: 'include'
        });

        if (response.ok) {
          setStatus('success');
          // Auto-redirect after success
          setTimeout(() => {
            navigate('/dashboard', { replace: true });
          }, 3000);
        } else {
          const error = await response.json();
          setStatus('error');
          setErrorMessage(error.detail || 'Verification failed. Please try again.');
        }
      } catch (error) {
        console.error('Verification error:', error);
        setStatus('error');
        setErrorMessage('Something went wrong. Please try again.');
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen bg-[#050505] flex flex-col">
      {/* Header */}
      <header className="p-6">
        <Link to="/" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="font-mono text-sm">Back</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          {/* Logo */}
          <div className="text-center mb-8">
            <img 
              src={LOGO_URL} 
              alt="Blackified" 
              className="h-16 md:h-20 mx-auto mb-4 object-contain"
            />
          </div>

          {/* Status Card */}
          <div className="glass-card rounded-xl p-8 text-center">
            {status === 'verifying' && (
              <>
                <div className="w-16 h-16 rounded-full bg-blue-500/10 flex items-center justify-center mx-auto mb-6">
                  <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Verifying Email</h2>
                <p className="text-zinc-400">
                  Please wait while we verify your email address...
                </p>
              </>
            )}

            {status === 'success' && (
              <>
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Email Verified!</h2>
                <p className="text-zinc-400 mb-6">
                  Your email has been verified successfully. You are now logged in and will be redirected to your dashboard.
                </p>
                <div className="flex items-center justify-center gap-2 text-green-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span className="text-sm">Redirecting...</span>
                </div>
              </>
            )}

            {status === 'error' && (
              <>
                <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
                  <XCircle className="w-8 h-8 text-red-400" />
                </div>
                <h2 className="text-2xl font-bold text-white mb-4">Verification Failed</h2>
                <p className="text-zinc-400 mb-6">
                  {errorMessage}
                </p>
                <div className="space-y-3">
                  <Link
                    to="/auth"
                    className="block w-full py-3 px-4 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors text-center"
                    data-testid="back-to-signin"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </>
            )}
          </div>
        </motion.div>
      </main>
    </div>
  );
}
